import type { PackageItemFormData } from "@src/common/components/PackageDimensionsForm/PackageDimensionsForm";
import { PackageTypeDefaultWeightApplication } from "@api/graphql/generated/generated-types";

export interface PackageTypeDefaultDimensions {
  defaultLength?: number | null;
  defaultWidth?: number | null;
  defaultHeight?: number | null;
  defaultWeight?: number | null;
  defaultWeightApplication?: PackageTypeDefaultWeightApplication | null;
}

/**
 * LOCKED binds only when the type carries a weight to lock to — cube rejects
 * `LOCKED` with a null `defaultWeight` on both types and overrides. Rows can
 * still meet that combination from data written before the validator, and
 * locking those inputs would strand whatever weight the row already held.
 */
export function isWeightLocked(
  defaults: PackageTypeDefaultDimensions | null | undefined,
): boolean {
  return (
    defaults?.defaultWeightApplication ===
      PackageTypeDefaultWeightApplication.Locked &&
    defaults.defaultWeight != null
  );
}

interface IsPackageItemWeightLockedArgs {
  packageTypeId?: string | null;
  packageTypes?: Array<{ id: string } & PackageTypeDefaultDimensions> | null;
}

/** A row with no selected Type never inherits a lock from the shipment. */
export function isPackageItemWeightLocked({
  packageTypeId,
  packageTypes,
}: IsPackageItemWeightLockedArgs): boolean {
  if (!packageTypeId) return false;
  return isWeightLocked(
    packageTypes?.find((type) => type.id === packageTypeId),
  );
}

/** True only when length, width, and height are all absent. Zero is entered. */
export function isDimensionless(pkg: PackageItemFormData): boolean {
  return pkg.length == null && pkg.width == null && pkg.height == null;
}

function isWeightless(pkg: PackageItemFormData): boolean {
  return pkg.weight == null;
}

export function applyDefaultsToPackage(
  pkg: PackageItemFormData,
  defaults: PackageTypeDefaultDimensions | null | undefined,
): PackageItemFormData {
  if (!defaults) {
    return pkg;
  }
  const fillDims = isDimensionless(pkg);
  const fillWeight = isWeightLocked(defaults) || isWeightless(pkg);
  if (!fillDims && !fillWeight) {
    return pkg;
  }
  const next: PackageItemFormData = {
    ...pkg,
    ...(fillDims
      ? {
          length: defaults.defaultLength ?? null,
          width: defaults.defaultWidth ?? null,
          height: defaults.defaultHeight ?? null,
        }
      : {}),
    ...(fillWeight ? { weight: defaults.defaultWeight ?? null } : {}),
  };
  if (
    next.length === pkg.length &&
    next.width === pkg.width &&
    next.height === pkg.height &&
    next.weight === pkg.weight
  ) {
    return pkg;
  }
  return next;
}

/** Override when set; otherwise the shipment package type. */
export function resolveEffectivePackageTypeId(
  itemPackageTypeId: string | null | undefined,
  shipmentPackageTypeId: string | null | undefined,
): string | null {
  return itemPackageTypeId ?? shipmentPackageTypeId ?? null;
}

/**
 * Certification contributors: shipment type (if set) plus each item override
 * (if set). Unique. Shipment type still counts when every row has an override.
 */
export function contributingPackageTypeIds(
  shipmentPackageTypeId: string | null | undefined,
  items: { packageTypeId?: string | null }[] | undefined | null,
): string[] {
  const ids: string[] = [];
  if (shipmentPackageTypeId) ids.push(shipmentPackageTypeId);
  for (const item of items ?? []) {
    if (item.packageTypeId) ids.push(item.packageTypeId);
  }
  return Array.from(new Set(ids));
}

interface PackageTypeWithIdAndDefaults extends PackageTypeDefaultDimensions {
  id: string;
}

/** The effective type of each row (override ?: shipment type). */
export function effectiveTypeIdsForPackageItems(
  items: PackageItemFormData[] | undefined | null,
  shipmentPackageTypeId: string | null | undefined,
): (string | null)[] {
  return (items ?? []).map((pkg) =>
    resolveEffectivePackageTypeId(pkg.packageTypeId, shipmentPackageTypeId),
  );
}

/**
 * Fill empty dim and weight fields from the given effective type. Dims fill
 * when the row is dimensionless; weight fills when weight is empty. Neither
 * gates the other. Leaves the row alone when no type resolves or the type is
 * not in the list.
 *
 * Pass the type the row is coming from. A weight held by a LOCKED type was put
 * there by that type, never typed, so leaving it drops the weight rather than
 * treating it as user input the new type's defaults must not overwrite.
 */
export function applyDefaultsForPackageType(
  pkg: PackageItemFormData,
  effectiveTypeId: string | null,
  packageTypes: PackageTypeWithIdAndDefaults[] | undefined | null,
  previousTypeId: string | null = null,
): PackageItemFormData {
  const base: PackageItemFormData = isPackageItemWeightLocked({
    packageTypeId: previousTypeId,
    packageTypes,
  })
    ? { ...pkg, weight: null }
    : pkg;
  if (!effectiveTypeId || !packageTypes) {
    return base;
  }
  return applyDefaultsToPackage(
    base,
    packageTypes.find((type) => type.id === effectiveTypeId),
  );
}

export function newPackageWithDefaults(
  defaults: PackageTypeDefaultDimensions | null | undefined,
): PackageItemFormData {
  return {
    length: defaults?.defaultLength ?? null,
    width: defaults?.defaultWidth ?? null,
    height: defaults?.defaultHeight ?? null,
    weight: defaults?.defaultWeight ?? null,
    packageTypeId: null,
    // Left unset so an untouched new row still reads as empty and is dropped on save;
    // the input shows 1 as its placeholder and the row counts as one piece either way.
    quantity: null,
  };
}

export function packageItemsChanged(
  before: PackageItemFormData[],
  after: PackageItemFormData[],
): boolean {
  if (before.length !== after.length) {
    return true;
  }
  return before.some((pkg, index) => {
    const next = after[index];
    return (
      pkg.length !== next.length ||
      pkg.width !== next.width ||
      pkg.height !== next.height ||
      pkg.weight !== next.weight ||
      pkg.packageTypeId !== next.packageTypeId ||
      pkg.quantity !== next.quantity
    );
  });
}
